import { useCallback, useMemo } from 'react';

import { useUrlSyncedTableState } from 'design-system';

import type { SortingState } from '@tanstack/react-table';
import { useSearchParams } from 'react-router';

export const SORT_PARAM = 'sort';

export const useCatalogSorting = (
  isForwardCurves: boolean,
  disableSearchParams: boolean,
  search: string | null
) => {
  const [searchParams, setRouterSearchParams] = useSearchParams();
  const setSearchParams = useCallback(
    (updater: (prev: URLSearchParams) => URLSearchParams) => {
      setRouterSearchParams(updater, { replace: true });
    },
    [setRouterSearchParams]
  );

  // `filterableColumnIds: []`: this hook is layered for its sorting half only —
  // catalog's own `columnFilters` stay owned by `useCatalogColumnFilters`/
  // `useFilter()`, whose URL shape (saved filters, `only`, `newData`, code,
  // IndexModule presets) this generic per-column-param model doesn't cover.
  const { sorting: urlSyncedSorting, onSortingChange } = useUrlSyncedTableState({
    searchParams,
    setSearchParams,
    filterableColumnIds: [],
    disableSearchParams
  });

  const isSearchActive = Boolean(search?.trim());
  const defaultSorting = useMemo<SortingState>(
    () => (isSearchActive || isForwardCurves ? [] : [{ id: 'Alias', desc: false }]),
    [isForwardCurves, isSearchActive]
  );

  // An empty result from `useUrlSyncedTableState` (no/malformed `sort` param,
  // or cleared local state) reads as "use the catalog default", matching the
  // previous `parseSorting(..., defaultSorting)` fallback behavior.
  //
  // `useUrlSyncedTableState`'s URL-parsing branch calls `parseSorting(searchParams)`
  // fresh on every render — a new array/object even when the URL hasn't
  // changed. Passed straight through as `useDataTable`'s controlled
  // `state.sorting`, that reference churn combines with `useTable`'s
  // per-render `setOptions` call to loop forever (confirmed via a minimal
  // repro: a controlled state slice that's a fresh array every render hangs
  // the table the moment any *other* re-render fires, e.g. a visibility
  // toggle). Re-stabilize the reference here, keyed off its own serialized
  // value so identical sorting content reuses the same array across renders.
  const sortingKey = JSON.stringify(urlSyncedSorting);
  const sorting = useMemo(
    () => (urlSyncedSorting.length ? urlSyncedSorting : defaultSorting),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sortingKey stands in for urlSyncedSorting's content
    [sortingKey, defaultSorting]
  );

  return { sorting, onSortingChange };
};
